import React, { useState } from "react";

import MailIcon from "@uui/Mail01";
import SendIcon from "@uui/Send01";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import type { TopicReportDistributionChannel } from "@app/analytics/TopicReport/types";
import { MESSAGING_BOT_PROVIDERS } from "@app/common/constants/providers";
import { getIntegrationConnectUrl } from "@app/common/utils/providerUtils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Row } from "@rui/Stack";

import SendViaDialog from "./SendViaDialog";

const ConnectButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#FFF",
  borderColor: theme.palette.shades.neutral[100],
  minWidth: 0,
  padding: theme.spacing(0.5, 1),
  minHeight: "30px",
  pointerEvents: "all",
  "&:hover": {
    backgroundColor: "#FFF",
  },
}));

const ProviderMenuItem = styled(MenuItem)(({ theme }) => ({
  "&.Mui-disabled": {
    opacity: 1,
    backgroundColor: theme.palette.background.default,
    "& .provider-menu-item__content": {
      opacity: 0.7,
    },
  },
}));

const SendVia = () => {
  const { anchorElement, open, onClose, onClick } = useAnchorElement();
  const { getIntegration } = useIntegrations();

  const [selectedChannel, setSelectedChannel] =
    useState<TopicReportDistributionChannel | null>(null);

  const handleSelectChannel =
    (channel: TopicReportDistributionChannel) => () => {
      setSelectedChannel(channel);
      onClose();
    };

  const handleCloseDialog = () => setSelectedChannel(null);

  return (
    <>
      <HeaderButton
        aria-controls={open ? "send-via-menu" : undefined}
        aria-haspopup
        aria-expanded={open ? "true" : undefined}
        primary
        startIcon={<SendIcon />}
        onClick={onClick}
      >
        Send via...
      </HeaderButton>
      <Menu
        id="send-via-menu"
        anchorEl={anchorElement}
        open={open}
        onClose={onClose}
      >
        {MESSAGING_BOT_PROVIDERS.map((provider) => {
          const integration = getIntegration({ providerId: provider.id })!;

          return (
            <ProviderMenuItem
              key={provider.id}
              disabled={!integration.isConnected || integration.isDisabled}
              onClick={handleSelectChannel(provider.id)}
            >
              <Row variant="apart" spacing={2}>
                <Row
                  variant="left"
                  spacing={1.5}
                  className="provider-menu-item__content"
                >
                  <Box
                    component="img"
                    src={provider.icon}
                    height="24px"
                    width="24px"
                  />
                  <Typography
                    variant="bodySecondaryReg"
                    color="shades.neutral.700"
                    marginRight={1}
                  >
                    {provider.displayNameShort}
                  </Typography>
                </Row>
                {!integration.isConnected && !integration.isDisabled && (
                  <ConnectButton
                    variant="outlined"
                    href={getIntegrationConnectUrl({
                      action: provider.connectAction,
                      returnRoute: location.pathname,
                      route: provider.connectUrl,
                      scope: "user",
                    })}
                  >
                    Connect
                  </ConnectButton>
                )}
              </Row>
            </ProviderMenuItem>
          );
        })}
        <MenuItem onClick={handleSelectChannel("email")}>
          <Row variant="left" spacing={1.5}>
            <MailIcon sx={{ fontSize: "24px", color: "shades.neutral.500" }} />
            <Typography variant="bodySecondaryReg" color="shades.neutral.700">
              Email
            </Typography>
          </Row>
        </MenuItem>
      </Menu>
      {selectedChannel != null && (
        <SendViaDialog channel={selectedChannel} onClose={handleCloseDialog} />
      )}
    </>
  );
};

export default SendVia;
